import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  ReportingCycle,
  ReportingCycleEnum,
} from "../api/models/reportingCycle.models";
import useReportingCycle from "../hooks/useReportingCycle";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useAuth } from "./authContext";
import { useNavigate } from "react-router";

type ReportingCycleContextProps = {
  reportingCycle?: ReportingCycleEnum;
  reportingCycleNotSelected: boolean;
  setReportingCycle: Dispatch<SetStateAction<ReportingCycleEnum | undefined>>;
  setReportingCycleNotSelected: Dispatch<SetStateAction<boolean>>;
  isLoadingReportingCycle: boolean;
  reportingCycleData?: ReportingCycle[];
};

export const ReportingCycleContext = createContext<ReportingCycleContextProps>({
  reportingCycle: undefined,
  reportingCycleNotSelected: true,
  setReportingCycle: () => {},
  setReportingCycleNotSelected: () => {},
  isLoadingReportingCycle: false,
  reportingCycleData: undefined,
});

export const ReportingCycleProvider = ({ children }) => {
  const navigate = useNavigate();
  const [companyId] = useUIStore(
    useShallow((state: UIState) => [state.companyId])
  );
  const [reportingCycle, setReportingCycle] = useState<
    ReportingCycleEnum | undefined
  >(undefined);
  const [selectedYearForNewReport] = useUIStore(
    useShallow((state: UIState) => [state.selectedYearForNewReport])
  );
  const [reportingCycleNotSelected, setReportingCycleNotSelected] =
    useState<boolean>(true);

  const { isLoggedIn } = useAuth();

  const { reportingCycleData, isLoadingReportingCycle } = useReportingCycle({
    companyId: companyId as number,
    enabled: !!companyId && isLoggedIn,
  });

  useEffect(() => {
    if (reportingCycleData && selectedYearForNewReport) {
      const cycleForSelectedYear = reportingCycleData.find(
        (cycle) => cycle.year === selectedYearForNewReport
      );

      if (cycleForSelectedYear) {
        setReportingCycle(cycleForSelectedYear.cycle);
        setReportingCycleNotSelected(false);
      }
    }
  }, [reportingCycleData, selectedYearForNewReport]);

  useEffect(() => {
    if (
      isLoggedIn &&
      reportingCycleData &&
      reportingCycleData.find(
        (cycle) => cycle.year === new Date().getFullYear()
      ) === undefined
    ) {
      navigate("/organizations");
    }
  }, [isLoggedIn, reportingCycleData]);

  const value: ReportingCycleContextProps = {
    isLoadingReportingCycle,
    reportingCycle,
    reportingCycleNotSelected,
    setReportingCycle,
    setReportingCycleNotSelected,
    reportingCycleData,
  };

  return (
    <ReportingCycleContext.Provider value={value}>
      {children}
    </ReportingCycleContext.Provider>
  );
};

export const useReportingCycleContext = () => useContext(ReportingCycleContext);
